import { motion } from "framer-motion";
import { Mountain, Landmark, Binoculars, Leaf } from "lucide-react";

const knowledge = [
  {
    icon: Mountain,
    title: "Geology & Deep Time",
    body: "Three hundred million years of Triassic sandstone, coal seams and basalt caps — why the cliffs glow orange at dusk and why the haze that names the Mountains is really eucalyptus oil in the air.",
  },
  {
    icon: Landmark,
    title: "Aboriginal Heritage",
    body: "Gundungurra and Dharug country for tens of thousands of years. The songlines, stories and sacred places behind the Three Sisters, the Megalong and the Grose — told with respect.",
  },
  {
    icon: Binoculars,
    title: "Hidden Lookouts & Timing",
    body: "Not Echo Point at noon. The quiet ledges, the west-facing escarpments at golden hour, the valleys that hold morning mist — and the back roads that skip the tour-bus queues at Jenolan.",
  },
  {
    icon: Leaf,
    title: "Flora, Fauna & Seasons",
    body: "Lyrebirds at first light, waratahs in October, glow worms after rain, snow in July. Every season changes what's worth seeing — and Daniel times your day around it.",
  },
];

export default function Expertise() {
  return (
    <section
      id="expertise"
      className="py-32 max-w-7xl mx-auto px-6 lg:px-10"
      data-testid="expertise-section"
    >
      <motion.div
        initial={{ opacity: 0, y: 40 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-100px" }}
        transition={{ duration: 0.9 }}
        className="mb-20 flex flex-wrap items-end justify-between gap-8"
      >
        <div>
          <p className="text-xs tracking-[0.35em] uppercase text-[#d4af37] mb-6">
            Expert Knowledge
          </p>
          <h2 className="font-serif-lux text-3xl sm:text-4xl lg:text-5xl font-light tracking-tight max-w-2xl">
            A guide, not just a driver.
          </h2>
        </div>
        <p className="max-w-sm text-base leading-relaxed tracking-wide text-[#888]">
          Anyone can follow a highway. Daniel reads the Mountains — their
          stone, their stories, their seasons — and builds your day around
          what they'll show you.
        </p>
      </motion.div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-white/10 border border-white/10">
        {knowledge.map((k, i) => (
          <motion.div
            key={k.title}
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.8, delay: (i % 2) * 0.1 }}
            className="group bg-[#050505] p-10 lg:p-14 hover:bg-[#0a0a0a] transition-colors duration-500"
            data-testid={`expertise-card-${i + 1}`}
          >
            <span className="inline-flex w-11 h-11 border border-[#d4af37]/40 items-center justify-center text-[#d4af37]">
              <k.icon size={18} strokeWidth={1.25} />
            </span>
            <h3 className="mt-8 font-serif-lux text-2xl sm:text-3xl font-light tracking-tight">
              {k.title}
            </h3>
            <p className="mt-5 text-sm leading-relaxed tracking-wide text-[#888]">
              {k.body}
            </p>
          </motion.div>
        ))}
      </div>
    </section>
  );
}
